import { useCallback, useEffect, useRef, useState } from "react";

export type ToastItem = {
  id: number;
  text: string;
  action?: { label: string; onClick: () => void };
};
const MAX_VISIBLE = 2;
const LIFETIME = 4500;
const LIFETIME_WITH_ACTION = 7000;

/** Room-level toasts. At most two are visible; each dismisses itself. Callers must only
 * pass public text (names, game titles) — never a role, word, hand or ballot. */
export function useToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);
  const push = useCallback(
    (text: string, action?: ToastItem["action"]) => {
      const id = next.current++;
      setToasts((list) => {
        const kept = list.filter((t) => t.text !== text);
        const dropped = kept.slice(0, Math.max(0, kept.length - (MAX_VISIBLE - 1)));
        for (const t of dropped) {
          clearTimeout(timers.current.get(t.id));
          timers.current.delete(t.id);
        }
        return [...kept.slice(dropped.length), { id, text, action }];
      });
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), action ? LIFETIME_WITH_ACTION : LIFETIME),
      );
      return id;
    },
    [dismiss],
  );
  useEffect(() => {
    const live = timers.current;
    return () => {
      for (const t of live.values()) clearTimeout(t);
      live.clear();
    };
  }, []);
  return { toasts, push, dismiss };
}

/** Always-mounted polite live region, so the first toast is announced too. */
export function ToastRegion({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div className="toast-region" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div className="toast" key={t.id}>
          <span className="toast-text">{t.text}</span>
          {t.action ? (
            <button
              type="button"
              className="toast-action"
              onClick={() => {
                t.action!.onClick();
                onDismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          ) : null}
          <button
            type="button"
            className="toast-close"
            aria-label="Dismiss"
            onClick={() => onDismiss(t.id)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="m6 6 12 12M6 18 18 6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
